import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { closeHistory } from '@atlaskit/prosemirror-history';

import { CONFIG_ITEM_KEYS } from '../../../ui/prebuilt/config-items/config-item-keys';
import type { EditorPluginAIConfigItem } from '../../../ui/prebuilt/config-items/config-items';
import { startWatchingAnnotationChanges } from '../../ai-annotation-watchmen/transforms';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { initPositions } from '../transforms';
import { validatePositions } from '../utils/positionValidation';

/**
 * Builds a transaction to initialize streaming decorations and state.
 * Returns null if:
 * - No positions or config item available
 * - Waiting for orchestrator to set correct positions (deferred mount)
 * - Unable to slice the original user content
 */
export function buildMountTransaction(state: EditorState): Transaction | null {
	const decorationPluginState = aiExperienceDecorationPluginKey.getState(state);
	const { positions, configItem, lastTriggeredFrom } = decorationPluginState ?? {};

	if (!positions || !configItem) {
		return null;
	}

	// Defer mounting for orchestrator flows until streaming API sets correct positions
	// (e.g., end of document for appendNodeToEnd)
	if (lastTriggeredFrom && lastTriggeredFrom !== 'streamingApi') {
		return null;
	}
	const [from, to] = positions;

	const validation = validatePositions(state, from, to);

	const docSize = state.doc.content.size;
	const isEmptySelection = from === to;
	const shouldReplaceDoc = isEmptySelection && !configItem?.insertOnEmptySelection;

	const [start, end] = shouldReplaceDoc
		? [1, Math.max(1, docSize - 1)]
		: [validation.sanitized.startPos, validation.sanitized.endPos];

	const isSuggestTitle = isSuggestTitleForConfluence(configItem);
	const showTelepointer = !isSuggestTitle;
	const showHighlight = !isSuggestTitle;
	const showAIContentHighlight = !shouldReplaceDoc;

	let originalUserContentSlice;
	try {
		originalUserContentSlice = state.doc.slice(start, end);
	} catch {
		return null;
	}

	let telepointerPos = start;
	try {
		const $start = state.doc.resolve(start);
		telepointerPos = $start.pos;
		let node = $start.node()?.firstChild;
		while (node && node.firstChild && !node.isLeaf) {
			telepointerPos += 1;
			node = node.firstChild;
		}
	} catch {
		telepointerPos = start;
	}

	let tr = state.tr;
	tr = closeHistory(tr);

	tr = startWatchingAnnotationChanges({ from: start, to: end })(tr);

	tr = initPositions({
		start,
		end,
		showHighlight: showHighlight && decorationPluginState?.withStreamingHighlight !== false,
		showAIContentHighlight,
		showTelepointer,
		telepointerPos,
		originalUserContentSlice,
		streamStartPos: start,
		sessionId: decorationPluginState?.currentSessionId,
	})(tr);

	return tr;
}

function isSuggestTitleForConfluence(configItem: EditorPluginAIConfigItem): boolean {
	if (!configItem) {
		return false;
	}
	return configItem.key === CONFIG_ITEM_KEYS.SUGGEST_A_TITLE;
}
